import type {
  StoreClassroomConversationInvitationRequest,
  StoreClassroomConversationRequest,
  StoreClassroomConversationRequestLinksItem,
} from "api/generated";
import type {
  ClassroomConversationInvitationFormData,
  ConversationFormData,
  ConversationMediaFormData,
  ConversationQuestionFormData,
} from "conversations/types";
import { toSchoolClock } from "shared/utils";

/**
 * A create may not carry media ids — there is nothing for them to belong to yet, and the
 * backend rejects the key outright.
 */
const mediaToDto = (
  media: ConversationMediaFormData,
): StoreClassroomConversationRequestLinksItem => ({
  name: media.name,
  url: media.url,
});

/** Options only travel with a radio question; any other type is a 422 with them. */
const questionToDto = (question: ConversationQuestionFormData) => {
  const base = {
    question: question.question,
    type: question.type,
    required: question.required,
    order: question.order,
  };

  return question.type === "radio"
    ? { ...base, options: question.options }
    : base;
};

/**
 * A gespreksverslag added from a classroom tab.
 *
 * `subject_shortage` is deliberately absent: the class stamps it server-side
 * (MEETING-CLASSROOM-CONVERSATION-SUBJECT-LOCK-001), and the endpoint does not accept it.
 *
 * `date` is sent as the Dutch wall-clock reading of the instant ("YYYY-MM-DD HH:mm:ss"),
 * which is what the backend stores. Sending an ISO instant would shift the time.
 */
export const classroomConversationFormDataToStoreDto = (
  formData: ConversationFormData,
): StoreClassroomConversationRequest => ({
  student_id: Number(formData.studentId),
  conversation_type_id: Number(formData.conversationTypeId),
  name: formData.name,
  teacher_notes: formData.teacherNotes,
  date: formData.date === null ? "" : toSchoolClock(formData.date),
  links: formData.links.map(mediaToDto),
  videos: formData.videos.map(mediaToDto),
});

/**
 * A gesprek planned from a classroom tab.
 *
 * `student_ids` is omitted entirely for "hele klas" — its absence is what tells the
 * endpoint to resolve the whole class. Sending an empty array would mean the same thing,
 * but omitting it says so without relying on the backend's empty-array handling.
 */
export const classroomConversationInvitationFormDataToStoreDto = (
  formData: ClassroomConversationInvitationFormData,
): StoreClassroomConversationInvitationRequest => ({
  conversation_type_id: Number(formData.conversationTypeId),
  name: formData.name,
  description: formData.description === "" ? null : formData.description,
  date: formData.date === null ? "" : toSchoolClock(formData.date),
  end_date: formData.endDate === null ? null : toSchoolClock(formData.endDate),
  attachment_allowed: formData.attachmentAllowed,
  attachment_required:
    formData.attachmentAllowed === true ? formData.attachmentRequired : false,
  ...(formData.studentIds === null ? {} : { student_ids: formData.studentIds }),
  questions: formData.questions.map(questionToDto),
  links: formData.links.map(mediaToDto),
  videos: formData.videos.map(mediaToDto),
});
